/**
 * Lightweight storefront preview — modal vs inline chrome + game thumbnail.
 * Decorative; not wired to runtime mount.
 */
import { Text } from "@shopify/polaris";
import { WheelGamePreview } from "../games/previews/WheelGamePreview";
import {
  ScratchGamePreview,
  resolveScratchPreviewTheme,
} from "../games/previews/ScratchGamePreview";
import type { Campaign, CampaignVariant } from "../../api/campaigns";

const WHEEL_KEY = "wheel_of_fortune";
const SCRATCH_KEY = "scratch_card";

export interface CampaignPreviewProps {
  campaign: Pick<Campaign, "displayMode" | "placement" | "name">;
  variant?: CampaignVariant | null;
}

function GamePreviewLarge({ variant }: { variant: CampaignVariant }) {
  const key = variant.game?.key ?? variant.gameId;
  if (key === WHEEL_KEY) {
    return (
      <div style={{ maxWidth: 240, margin: "0 auto" }}>
        <WheelGamePreview />
      </div>
    );
  }
  if (key === SCRATCH_KEY) {
    return (
      <div style={{ maxWidth: 240, margin: "0 auto" }}>
        <ScratchGamePreview theme={resolveScratchPreviewTheme(variant.config)} />
      </div>
    );
  }
  return (
    <Text as="p" variant="bodySm" tone="subdued">
      Preview for this game type is not available yet.
    </Text>
  );
}

export function CampaignPreview({ campaign, variant }: CampaignPreviewProps) {
  const mode = campaign.displayMode ?? "modal";

  const inner = !variant ? (
    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
      Add a variant with a game to see a live preview.
    </Text>
  ) : (
    <>
      <GamePreviewLarge variant={variant} />
      <p
        style={{
          margin: "12px 0 0",
          fontSize: "0.75rem",
          color: "var(--p-color-text-secondary)",
          textAlign: "center",
        }}
      >
        Example reward will appear here
      </p>
    </>
  );

  return (
    <div
      style={{
        background: "var(--p-color-bg-surface-secondary)",
        borderRadius: "12px",
        padding: "16px",
        border: "1px solid var(--p-color-border)",
      }}
    >
      {mode === "modal" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            padding: "12px",
          }}
        >
          <div
            style={{
              background: "var(--p-color-bg-surface)",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 8px 28px rgba(32, 34, 35, 0.18)",
              maxWidth: 320,
              width: "100%",
            }}
          >
            {inner}
          </div>
        </div>
      ) : (
        <div
          style={{
            background: "var(--p-color-bg-surface)",
            borderRadius: "10px",
            padding: "14px",
            border: "1px solid var(--p-color-border)",
          }}
        >
          {inner}
        </div>
      )}
    </div>
  );
}
